import { ShoppingCart, DollarSign, RefreshCw, Package, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SaleTotalOrderTodayProps {
    totalOrders: number;
    totalItems?: number;
    paidAmount: number;
    currency?: string;
    className?: string;
    onRefresh?: () => void;
    isLoading?: boolean;
}

export function SaleTotalOrderToday({
    totalOrders,
    totalItems,
    paidAmount,
    currency = "$",
    className,
    onRefresh,
    isLoading,
}: SaleTotalOrderTodayProps) {
    return (
        <Card className={cn("p-1.5 border border-slate-200 shadow-sm h-9 flex items-center", className)}>
            <div className="flex items-center gap-2 sm:gap-3">
                {/* Total Orders */}
                <div className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-50 rounded">
                    <ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                    <div className="flex items-center gap-1 sm:gap-1.5">
                        <span className="text-[9px] sm:text-[10px] font-medium text-blue-600">Orders:</span>
                        <span className="text-xs sm:text-sm font-bold text-blue-900">
                            {totalOrders.toLocaleString()}
                        </span>
                    </div>
                </div>

                {/* Divider */}
                <div className="h-5 sm:h-6 w-px bg-slate-200" />

                {/* Total Items */}
                {totalItems !== undefined && (
                    <>
                        <div className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-50 rounded">
                            <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                            <div className="flex items-center gap-1 sm:gap-1.5">
                                <span className="text-[9px] sm:text-[10px] font-medium text-blue-600">Items:</span>
                                <span className="text-xs sm:text-sm font-bold text-blue-900">
                                    {totalItems.toLocaleString()}
                                </span>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="h-5 sm:h-6 w-px bg-slate-200" />
                    </>
                )}

                {/* Paid Amount */}
                <div className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-emerald-50 rounded">
                    <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" />
                    <div className="flex items-center gap-1 sm:gap-1.5">
                        <span className="text-[9px] sm:text-[10px] font-medium text-emerald-600">Paid:</span>
                        <span className="text-xs sm:text-sm font-bold text-emerald-900">
                            {currency}{Number(paidAmount || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </span>
                    </div>
                </div>


                {/* Refresh Button */}
                {onRefresh && (
                    <>
                        <div className="h-5 sm:h-6 w-px bg-slate-200" />
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 sm:h-7 sm:w-7 hover:bg-slate-100"
                            onClick={onRefresh}
                            title="Refresh totals"
                            disabled={isLoading}
                        >
                            {isLoading && <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-600" />}
                            {!isLoading && <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-600" />}

                        </Button>
                    </>
                )}
            </div>
        </Card >
    );
}